import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Globe2, Users, Radio, Loader2, ExternalLink } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell } from "recharts";
import { extrasApi } from "@/lib/extrasApi";
import { useI18n } from "@/lib/i18n";

// Small country-code → flag emoji helper (2-letter ISO)
function flag(cc) {
    if (!cc || cc.length !== 2 || cc === "??") return "🌐";
    const A = 127397;
    return String.fromCodePoint(cc.charCodeAt(0) + A) + String.fromCodePoint(cc.charCodeAt(1) + A);
}

export default function VisitorsPanel() {
    const { locale } = useI18n();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let live = true;
        async function load() {
            try {
                const d = await extrasApi.visits.summary();
                if (live) setData(d);
            } catch (_e) { /* silent */ }
            finally { if (live) setLoading(false); }
        }
        load();
        const t = setInterval(load, 15000); // refresh every 15s for "live" feel
        return () => { live = false; clearInterval(t); };
    }, []);

    const bl = (en, fr) => (locale === "fr" ? fr : en);

    return (
        <motion.section
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
            className="mt-8 rounded-2xl p-6 bg-brand-surface border border-white/8"
            data-testid="dashboard-visitors-panel"
        >
            <div className="flex items-start justify-between flex-wrap gap-3 mb-5">
                <div>
                    <div className="text-xs uppercase tracking-[0.2em] font-bold text-brand-cyan flex items-center gap-2">
                        <Globe2 size={13} /> {bl("MY ZONE — LIVE TRAFFIC", "MA ZONE — TRAFIC LIVE")}
                    </div>
                    <h2 className="mt-1 font-display font-black text-2xl tracking-tighter">
                        {bl("Who's visiting boxe-b2b.com", "Qui visite boxe-b2b.com")}
                    </h2>
                    <p className="mt-1 text-xs text-zinc-500">
                        {bl("Anonymous, GDPR-clean, updated every 15 seconds. No fake counter.",
                            "Anonyme, RGPD-clean, mis à jour toutes les 15 s. Aucun faux compteur.")}
                    </p>
                </div>
                {data && data.live > 0 && (
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-xs font-mono">
                        <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                        {data.live} {bl("live now", "en ligne")}
                    </div>
                )}
            </div>

            {loading || !data ? (
                <div className="py-16 flex items-center justify-center text-zinc-500">
                    <Loader2 className="animate-spin mr-2" /> {bl("Loading…", "Chargement…")}
                </div>
            ) : (
                <>
                    {/* KPI row */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
                        <Kpi label={bl("Live", "En ligne")} value={data.live} icon={Radio} tone="emerald" testKey="live" />
                        <Kpi label={bl("Visits (7d)", "Visites (7j)")} value={data.total_7d} icon={Users} tone="indigo" testKey="visits7" />
                        <Kpi label={bl("Unique (30d)", "Uniques (30j)")} value={data.unique_30d} icon={Users} tone="cyan" testKey="unique30" />
                        <Kpi label={bl("Total (30d)", "Total (30j)")} value={data.total_30d} icon={Users} tone="violet" testKey="total30" />
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                        {/* Chart */}
                        <div className="lg:col-span-2 rounded-xl bg-brand-void border border-white/5 p-4">
                            <div className="text-xs uppercase tracking-widest font-bold text-zinc-500 mb-3">
                                {bl("Last 7 days", "7 derniers jours")}
                            </div>
                            <div className="h-56">
                                <ResponsiveContainer width="100%" height="100%" minHeight={180}>
                                    <BarChart data={data.by_day} margin={{ top: 6, right: 4, left: -20, bottom: 0 }}>
                                        <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
                                        <XAxis dataKey="day" tick={{ fill: "#71717A", fontSize: 11 }} axisLine={false} tickLine={false} />
                                        <YAxis tick={{ fill: "#71717A", fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
                                        <Tooltip contentStyle={{ background: "#1A1A2E", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 12, color: "#fff" }} />
                                        <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                                            {data.by_day.map((_, i) => (
                                                <Cell key={i} fill={i === data.by_day.length - 1 ? "#06B6D4" : "#6366F1"} />
                                            ))}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>

                        {/* Countries */}
                        <div className="rounded-xl bg-brand-void border border-white/5 p-4">
                            <div className="text-xs uppercase tracking-widest font-bold text-zinc-500 mb-3">
                                {bl("Countries", "Pays")}
                            </div>
                            {data.top_countries.length === 0 ? (
                                <div className="text-xs text-zinc-500 py-4">{bl("No visitors yet.", "Aucun visiteur.")}</div>
                            ) : (
                                <ul className="space-y-2">
                                    {data.top_countries.map((c) => (
                                        <li key={c.country} className="flex items-center justify-between text-sm">
                                            <span className="flex items-center gap-2">
                                                <span className="text-lg">{flag(c.country)}</span>
                                                <span className="text-zinc-300 font-mono text-xs">{c.country}</span>
                                            </span>
                                            <span className="font-mono text-brand-cyan text-xs">{c.count}</span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>

                    {/* Pages + Referrers */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mt-5">
                        <div className="rounded-xl bg-brand-void border border-white/5 p-4">
                            <div className="text-xs uppercase tracking-widest font-bold text-zinc-500 mb-3">
                                {bl("Top pages", "Pages populaires")}
                            </div>
                            {data.top_pages.length === 0 ? (
                                <div className="text-xs text-zinc-500 py-4">{bl("No pageviews yet.", "Aucune vue.")}</div>
                            ) : (
                                <ul className="space-y-1.5">
                                    {data.top_pages.map((p) => (
                                        <li key={p.path} className="flex items-center justify-between gap-3 text-sm">
                                            <span className="truncate text-zinc-300 font-mono text-xs">{p.path}</span>
                                            <span className="font-mono text-brand-cyan text-xs shrink-0">{p.count}</span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>

                        <div className="rounded-xl bg-brand-void border border-white/5 p-4">
                            <div className="text-xs uppercase tracking-widest font-bold text-zinc-500 mb-3 flex items-center gap-2">
                                <ExternalLink size={11} /> {bl("Where they come from", "D'où ils viennent")}
                            </div>
                            {data.top_referrers.length === 0 ? (
                                <div className="text-xs text-zinc-500 py-4">{bl("Direct traffic only — share your link!", "Trafic direct uniquement — partage ton lien !")}</div>
                            ) : (
                                <ul className="space-y-1.5">
                                    {data.top_referrers.map((r, i) => (
                                        <li key={i} className="flex items-center justify-between gap-3 text-sm">
                                            <span className="truncate text-zinc-300 font-mono text-xs">{r.source}</span>
                                            <span className="font-mono text-brand-cyan text-xs shrink-0">{r.count}</span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>

                    <div className="mt-4 text-[11px] text-zinc-600 font-mono">
                        {bl("Ask the AI Copilot: \"How many visitors this week?\" — it now knows.",
                            "Demande au Copilote IA : « Combien de visiteurs cette semaine ? » — il sait maintenant.")}
                    </div>
                </>
            )}
        </motion.section>
    );
}

function Kpi({ label, value, icon: Icon, tone, testKey }) {
    return (
        <div className="rounded-xl p-4 bg-brand-void border border-white/5" data-testid={`visitors-kpi-${testKey}`}>
            <div className="flex items-start justify-between">
                <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-bold">{label}</div>
                <Icon size={13} className={`text-brand-${tone === "emerald" ? "cyan" : tone}`} />
            </div>
            <div className="mt-2 font-display font-black text-2xl tracking-tighter">{value}</div>
        </div>
    );
}
